import { useEffect, useState } from 'react'

import { Zone } from '@/core/api/entities/zone/zone'
import { useAudioVersions } from '@/core/hooks/useAudioVersion'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Alert, AlertDescription } from '@/shared/components/atoms/Alert'
import { Button } from '@/shared/components/atoms/Button'
import { RadioGroup, RadioGroupItem } from '@/shared/components/atoms/Radio'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface ChangeAdvertAudioVersionModalProps {
	visible: boolean
	zone: Zone | null
	onClose: () => void
	onConfirm: (advertAudioVersionId: number) => void
	isUpdating: boolean
}

export const ChangeAdvertAudioVersionModal = ({
	visible,
	zone,
	onClose,
	onConfirm,
	isUpdating,
}: ChangeAdvertAudioVersionModalProps) => {
	const { t } = useTranslation('zones')

	const { data: audioVersionsRaw = [], isLoading } = useAudioVersions({
		options: { enabled: visible },
	})

	const audioVersions = [...audioVersionsRaw].sort((a, b) => b.id - a.id)

	const defaultVersionId = zone?.advertAudioVersionId ?? audioVersions[0]?.id

	const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
		null
	)

	useEffect(() => {
		if (visible && audioVersions.length > 0) {
			setSelectedVersionId(defaultVersionId ?? null)
		}
	}, [visible, defaultVersionId, audioVersions.length])

	const handleConfirm = () => {
		if (selectedVersionId == null) return
		onConfirm(selectedVersionId)
	}

	const handleClose = () => {
		if (!isUpdating) {
			onClose()
		}
	}

	return (
		<Dialog open={visible} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-[500px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				{/* Header */}
				<div className="px-6 py-5 border-b">
					<DialogTitle className="text-lg">
						{t('modals.changeAdvertAudioVersion.title')}
					</DialogTitle>
				</div>

				{/* Content */}
				<div className="px-6 py-6 space-y-5 overflow-y-auto flex-1">
					{/* Resync notice */}
					<Alert variant="warning">
						<AlertDescription>
							{t('modals.changeAdvertAudioVersion.resyncNotice')}
						</AlertDescription>
					</Alert>

					<p className="text-sm text-muted-foreground">
						{t('modals.changeAdvertAudioVersion.description')}
					</p>

					{/* Version options */}
					{isLoading ? (
						<div className="space-y-3">
							<Skeleton className="h-16 w-full rounded-lg" />
							<Skeleton className="h-16 w-full rounded-lg" />
						</div>
					) : (
						<RadioGroup
							value={selectedVersionId?.toString()}
							onValueChange={(value) => setSelectedVersionId(Number(value))}
							className="gap-3"
						>
							{audioVersions.map((version) => (
								<label
									key={version.id}
									htmlFor={`audio-version-${version.id}`}
									className={`flex items-start gap-3 cursor-pointer rounded-lg border p-4 transition-colors ${
										selectedVersionId === version.id
											? 'border-white/30 bg-white/5'
											: 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
									}`}
								>
									<RadioGroupItem
										id={`audio-version-${version.id}`}
										value={version.id.toString()}
										className="mt-0.5 flex-shrink-0"
									/>
									<div>
										<p className="text-sm font-medium">
											{t(
												`modals.changeAdvertAudioVersion.versions.${version.name}.label`,
												{ defaultValue: version.name }
											)}
										</p>
										<p className="text-xs text-muted-foreground mt-0.5">
											{t(
												`modals.changeAdvertAudioVersion.versions.${version.name}.description`,
												{ defaultValue: '' }
											)}
										</p>
									</div>
								</label>
							))}
						</RadioGroup>
					)}
				</div>

				{/* Footer */}
				<div className="px-6 py-5 border-t flex justify-end gap-3">
					<Button
						type="button"
						variant="outline"
						onClick={handleClose}
						disabled={isUpdating}
						className="rounded-full px-6"
					>
						{t('modals.changeAdvertAudioVersion.cancel')}
					</Button>
					<Button
						type="button"
						onClick={handleConfirm}
						disabled={isUpdating || selectedVersionId == null || isLoading}
						className="rounded-full px-6"
					>
						{isUpdating
							? t('modals.changeAdvertAudioVersion.updating')
							: t('modals.changeAdvertAudioVersion.confirm')}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
